import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, Play, Download, RefreshCw } from 'lucide-react';
import {
  getCronRuns,
  getCronStats,
  getUserStatusChanges,
  type CronRunsReport,
  type CronStatsReport,
  type UserStatusChangesReport,
  type CronRunRow,
  type CronJobStatRow,
  type UserStatusChangeRow,
  type CronJobRunStatus,
  type StatusLogActorType,
} from '@/api/cron';

interface CheckRunState<T> {
  loading: boolean;
  data: T | null;
  error: string | null;
}

const STATUS_VARIANT: Record<
  CronJobRunStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  succeeded: 'default',
  failed: 'destructive',
  running: 'secondary',
};

const ACTOR_VARIANT: Record<
  StatusLogActorType,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  admin: 'default',
  cron: 'secondary',
  webhook: 'outline',
  payment: 'outline',
  system: 'outline',
};

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function rowsToCsv(rows: any[], headers: string[]): string {
  const escape = (v: unknown) => {
    if (v === null || v === undefined) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(headers.map((h) => escape((r as any)[h])).join(','));
  }
  return lines.join('\n');
}

function fmtDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function shortJobName(name: string): string {
  const dot = name.indexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

/* ------------------------------------------------------------------ */
/* Per-job stats card                                                 */
/* ------------------------------------------------------------------ */

export function CronStatsCard() {
  const [state, setState] = useState<CheckRunState<CronStatsReport>>({
    loading: false,
    data: null,
    error: null,
  });

  const run = async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const data = await getCronStats(7);
      setState({ loading: false, data, error: null });
    } catch (err: any) {
      setState({
        loading: false,
        data: null,
        error: err?.message || 'Failed to load',
      });
    }
  };

  useEffect(() => {
    run();
  }, []);

  const { loading, data, error } = state;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Per-job stats (last 7 days)</CardTitle>
        <CardDescription>
          For each scheduled cron job: total runs, success rate, average duration,
          last run, and total items processed (sum of <code className="text-xs">counters.processed</code>).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={run} disabled={loading} variant="outline" size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
          {data && (
            <span className="text-xs text-muted-foreground">
              as of {data.today}
            </span>
          )}
        </div>

        {error && <div className="text-sm text-destructive">{error}</div>}

        {data && data.jobs.length === 0 && !loading && (
          <div className="text-sm text-muted-foreground">
            No cron runs recorded in the last 7 days.
          </div>
        )}

        {data && data.jobs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.jobs.map((j) => (
              <JobStatTile key={j.jobName} job={j} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function JobStatTile({ job }: { job: CronJobStatRow }) {
  return (
    <div className="border rounded-md p-3 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-sm truncate" title={job.jobName}>
          {shortJobName(job.jobName)}
        </div>
        {job.lastRunStatus && (
          <Badge variant={STATUS_VARIANT[job.lastRunStatus]} className="text-xs">
            {job.lastRunStatus}
          </Badge>
        )}
      </div>
      <div className="text-xs text-muted-foreground truncate" title={job.jobName}>
        {job.jobName}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs pt-1">
        <div>
          <span className="text-muted-foreground">Runs:</span>{' '}
          <span className="font-mono">{job.totalRuns}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Success:</span>{' '}
          <span className="font-mono">
            {(job.successRate * 100).toFixed(0)}%
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Avg dur:</span>{' '}
          <span className="font-mono">{fmtDuration(job.avgDurationMs)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Processed:</span>{' '}
          <span className="font-mono">{job.totalProcessed}</span>
        </div>
        <div className="col-span-2">
          <span className="text-muted-foreground">Last:</span>{' '}
          <span className="font-mono">{fmtDateTime(job.lastRunAt)}</span>
        </div>
        {job.failed > 0 && (
          <div className="col-span-2 text-destructive">
            {job.failed} failed run{job.failed === 1 ? '' : 's'}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Recent runs card                                                   */
/* ------------------------------------------------------------------ */

export function CronRunsCard() {
  const [state, setState] = useState<CheckRunState<CronRunsReport>>({
    loading: false,
    data: null,
    error: null,
  });
  const [statusFilter, setStatusFilter] = useState<CronJobRunStatus | 'all'>(
    'all',
  );
  const [days, setDays] = useState<number>(7);

  const run = async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const data = await getCronRuns({
        days,
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 200,
      });
      setState({ loading: false, data, error: null });
    } catch (err: any) {
      setState({
        loading: false,
        data: null,
        error: err?.message || 'Failed to load',
      });
    }
  };

  useEffect(() => {
    run();
  }, [statusFilter, days]);

  const { loading, data, error } = state;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent cron runs</CardTitle>
        <CardDescription>
          One row per cron-job execution per tenant. Counters are job-specific
          (e.g. <code className="text-xs">processed</code>, <code className="text-xs">errors</code>,{' '}
          <code className="text-xs">deactivated</code>).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="cron-runs-days" className="text-sm">
            Last
          </label>
          <select
            id="cron-runs-days"
            aria-label="Lookback window in days"
            title="Lookback window in days"
            className="border rounded-md px-2 py-1 text-sm bg-background"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            disabled={loading}
          >
            <option value={1}>1 day</option>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>

          <label htmlFor="cron-runs-status" className="text-sm ml-2">
            Status
          </label>
          <select
            id="cron-runs-status"
            aria-label="Filter by run status"
            title="Filter by run status"
            className="border rounded-md px-2 py-1 text-sm bg-background"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as CronJobRunStatus | 'all')
            }
            disabled={loading}
          >
            <option value="all">All</option>
            <option value="succeeded">succeeded</option>
            <option value="failed">failed</option>
            <option value="running">running</option>
          </select>

          <Button onClick={run} disabled={loading} variant="outline" size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>

          {data && (
            <Badge variant="outline">{data.total} runs</Badge>
          )}
        </div>

        {error && <div className="text-sm text-destructive">{error}</div>}

        {data && data.runs.length === 0 && !loading && (
          <div className="text-sm text-muted-foreground">
            No cron runs in this window.
          </div>
        )}

        {data && data.runs.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-sm">Runs ({data.runs.length})</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  downloadCsv(
                    `cron-runs-${data.today}.csv`,
                    rowsToCsv(data.runs, [
                      'runId',
                      'jobName',
                      'tenantId',
                      'startedAt',
                      'finishedAt',
                      'durationMs',
                      'status',
                      'counters',
                      'errorMessage',
                      'notes',
                    ]),
                  )
                }
              >
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
            </div>
            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Counters</TableHead>
                    <TableHead>Error / notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.runs.slice(0, 50).map((r) => (
                    <RunRow key={r.runId} run={r} />
                  ))}
                </TableBody>
              </Table>
            </div>
            {data.runs.length > 50 && (
              <div className="text-xs text-muted-foreground">
                Showing first 50 of {data.runs.length}. Download CSV for the full
                list.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RunRow({ run }: { run: CronRunRow }) {
  const countersStr = Object.entries(run.counters || {})
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ');
  return (
    <TableRow>
      <TableCell className="text-xs">
        <div className="font-medium">{shortJobName(run.jobName)}</div>
        <div className="text-muted-foreground font-mono">{run.jobName}</div>
      </TableCell>
      <TableCell className="font-mono text-xs">
        {fmtDateTime(run.startedAt)}
      </TableCell>
      <TableCell className="font-mono text-xs">
        {fmtDuration(run.durationMs)}
      </TableCell>
      <TableCell>
        <Badge variant={STATUS_VARIANT[run.status]}>{run.status}</Badge>
      </TableCell>
      <TableCell className="font-mono text-xs max-w-[260px] break-words">
        {countersStr || <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-xs max-w-[300px] break-words">
        {run.errorMessage ? (
          <span className="text-destructive">{run.errorMessage}</span>
        ) : run.notes ? (
          <span className="text-muted-foreground">{run.notes}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

/* ------------------------------------------------------------------ */
/* User status changes card                                           */
/* ------------------------------------------------------------------ */

export function UserStatusChangesCard() {
  const [state, setState] = useState<CheckRunState<UserStatusChangesReport>>({
    loading: false,
    data: null,
    error: null,
  });
  const [days, setDays] = useState<number>(7);
  const [userIdInput, setUserIdInput] = useState<string>('');
  const [appliedUserId, setAppliedUserId] = useState<string>('');

  const run = async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const data = await getUserStatusChanges({
        days: appliedUserId ? 365 : days,
        limit: appliedUserId ? 500 : 200,
        userId: appliedUserId || undefined,
      });
      setState({ loading: false, data, error: null });
    } catch (err: any) {
      setState({
        loading: false,
        data: null,
        error: err?.message || 'Failed to load',
      });
    }
  };

  useEffect(() => {
    run();
  }, [days, appliedUserId]);

  // Read userId from URL on mount (deep-link support: /cron?userId=<id>)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const u = params.get('userId');
    if (u) {
      setUserIdInput(u);
      setAppliedUserId(u);
    }
  }, []);

  const apply = () => {
    setAppliedUserId(userIdInput.trim());
  };

  const clear = () => {
    setUserIdInput('');
    setAppliedUserId('');
  };

  const { loading, data, error } = state;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent user status changes</CardTitle>
        <CardDescription>
          Activation / deactivation / soft-delete events with the reason and
          actor. Paste a userId to view a single user&apos;s full status history (up
          to 365 days back).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            placeholder="Filter by userId (paste ObjectId)"
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') apply();
            }}
            aria-label="Filter by userId"
            className="border rounded-md px-2 py-1 text-sm bg-background w-72 font-mono"
            disabled={loading}
          />
          <Button onClick={apply} disabled={loading} size="sm">
            <Play className="h-4 w-4 mr-2" />
            Apply
          </Button>
          {appliedUserId && (
            <Button onClick={clear} variant="outline" size="sm">
              Clear
            </Button>
          )}

          {!appliedUserId && (
            <>
              <label htmlFor="status-changes-days" className="text-sm ml-2">
                Last
              </label>
              <select
                id="status-changes-days"
                aria-label="Lookback window in days"
                title="Lookback window in days"
                className="border rounded-md px-2 py-1 text-sm bg-background"
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10))}
                disabled={loading}
              >
                <option value={1}>1 day</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
            </>
          )}

          {data && <Badge variant="outline">{data.total} events</Badge>}
          {appliedUserId && (
            <Badge variant="secondary">
              filtered: {appliedUserId.slice(0, 8)}…
            </Badge>
          )}
        </div>

        {error && <div className="text-sm text-destructive">{error}</div>}

        {data && data.changes.length === 0 && !loading && (
          <div className="text-sm text-muted-foreground">
            {appliedUserId
              ? 'No status events recorded for this user.'
              : 'No status events in this window.'}
          </div>
        )}

        {data && data.changes.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-sm">
                Events ({data.changes.length})
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  downloadCsv(
                    `user-status-changes-${data.today}.csv`,
                    rowsToCsv(data.changes, [
                      'logId',
                      'userId',
                      'name',
                      'surname',
                      'email',
                      'phone',
                      'from',
                      'to',
                      'reason',
                      'actorType',
                      'actorId',
                      'metadata',
                      'triggeredAt',
                    ]),
                  )
                }
              >
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
            </div>
            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Actor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.changes.slice(0, 100).map((c) => (
                    <ChangeRow key={c.logId} c={c} />
                  ))}
                </TableBody>
              </Table>
            </div>
            {data.changes.length > 100 && (
              <div className="text-xs text-muted-foreground">
                Showing first 100 of {data.changes.length}. Download CSV for the
                full list.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ChangeRow({ c }: { c: UserStatusChangeRow }) {
  return (
    <TableRow>
      <TableCell className="font-mono text-xs whitespace-nowrap">
        {fmtDateTime(c.triggeredAt)}
      </TableCell>
      <TableCell className="text-xs">
        <div>
          {c.surname} {c.name}
        </div>
        <div className="text-muted-foreground">{c.email || '—'}</div>
        <div className="text-muted-foreground font-mono text-[10px]">
          {c.userId}
        </div>
      </TableCell>
      <TableCell className="text-xs whitespace-nowrap">
        <Badge variant="outline">{c.from ?? 'new'}</Badge>
        <span className="mx-1">→</span>
        <Badge variant={c.to === 'deleted' ? 'destructive' : c.to === 'active' ? 'default' : 'secondary'}>
          {c.to}
        </Badge>
      </TableCell>
      <TableCell className="text-xs font-mono break-words max-w-[280px]">
        {c.reason}
      </TableCell>
      <TableCell className="text-xs">
        <Badge variant={ACTOR_VARIANT[c.actorType]}>{c.actorType}</Badge>
        {c.actorId && (
          <div className="text-muted-foreground font-mono text-[10px] mt-1">
            {c.actorId}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */

export default function CronPage() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">Cron</h1>
        <p className="text-muted-foreground">
          Cron-job activity (runs, outcomes, durations) and user
          activation/deactivation events for the current tenant. Reach this page
          by URL (<code className="text-xs">/cron</code>); it is not linked from
          the sidebar.
        </p>
      </div>

      <CronStatsCard />
      <CronRunsCard />
      <UserStatusChangesCard />
    </div>
  );
}
